import * as React from 'react';
import { Form, Field, FormRenderProps } from 'react-final-form';
import { ValidationErrors } from 'final-form';
import FontAwesomeIcon from 'react-fontawesome';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { CLOUDINARY_BASE_URL, OPEN_ORDER_PATH } from './Constants';
import { initialRequest } from './actions/Actions';
import { Store } from './types';

interface FormValues {
    username: string;
    password: string;
    storeId: string;
}

const Login: React.FC<RouteComponentProps> = ({ history }) => {
    const [loading, setLoading] = React.useState(false);
    const dispatch: ThunkDispatch<Store, unknown, AnyAction> = useDispatch();

    const renderForm = React.useCallback(
        ({ handleSubmit, submitting, validating }: FormRenderProps) => (
            <form className='login-form' onSubmit={handleSubmit}>
                <div className='title'>SCAN + ORDER BACKOFFICE</div>
                <Field name='storeId'>
                    {({ input, meta }) => (
                        <div className='field-input'>
                            <FontAwesomeIcon name='id-badge' className='input-icon' />
                            <input
                                {...input}
                                type='number'
                                placeholder='Store ID'
                                min={0}
                            />
                            {meta.error && meta.touched && (
                                <FontAwesomeIcon
                                    className='error-icon'
                                    name='exclamation-circle'
                                    title={meta.error}
                                />
                            )}
                        </div>
                    )}
                </Field>
                <Field name='username'>
                    {({ input, meta }) => (
                        <div className='field-input'>
                            <FontAwesomeIcon name='user' className='input-icon' />
                            <input {...input} type='text' placeholder='Username' />
                            {meta.error && meta.touched && (
                                <FontAwesomeIcon
                                    className='error-icon'
                                    name='exclamation-circle'
                                    title={meta.error}
                                />
                            )}
                        </div>
                    )}
                </Field>
                <Field name='password'>
                    {({ input, meta }) => (
                        <div className='field-input'>
                            <FontAwesomeIcon name='lock' className='input-icon' />
                            <input {...input} type='password' placeholder='Password' />
                            {meta.error && meta.touched && (
                                <FontAwesomeIcon
                                    className='error-icon'
                                    name='exclamation-circle'
                                    title={meta.error}
                                />
                            )}
                        </div>
                    )}
                </Field>
                <div className='submit-wrapper'>
                    {loading && <FontAwesomeIcon name='spinner' className='fa-spin' />}
                    <button
                        className='submit-button'
                        type='submit'
                        disabled={submitting || validating}>
                        Login
                    </button>
                </div>
                <img
                    src={`${CLOUDINARY_BASE_URL}/backoffice/powered_by_personalized.png`}
                />
            </form>
        ),
        [loading]
    );

    const validateInput = React.useCallback((values: FormValues): ValidationErrors => {
        const errors: ValidationErrors = {};

        if (values.storeId === undefined) {
            errors.storeId = 'Required field';
        }

        if (!values.username) {
            errors.username = 'Required field';
        }

        if (!values.password) {
            errors.password = 'Required field';
        }

        return errors;
    }, []);

    const handleLogin = React.useCallback(
        ({ username, password, storeId }: FormValues) => {
            setLoading(true);

            dispatch(initialRequest(username, password, Number(storeId)))
                .then(() => {
                    history.push(OPEN_ORDER_PATH);
                })
                .catch(() => {
                    setLoading(false);
                });
        },
        [dispatch, history]
    );

    return (
        <div className='login'>
            <Form onSubmit={handleLogin} render={renderForm} validate={validateInput} />
        </div>
    );
};

export default withRouter(Login);
